/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {AdobeMetricsRuntime} from './AdobeMetricsRuntime';
import uuidv4 from 'uuid/v4';

/**
 * Omega script integration functions for use with Adobe Analytics.
 *
 * @see {@link https://wiki.corp.adobe.com/display/OMEGA/Adobe+Data+Layer+Schema}.
 */

/* eslint-disable max-len */
/**
 * Initialize Omega Setup with digital data and tracking scripts in the
 * <code>HEAD</code> element in DOM.
 *
 * @param {string} script Optional tracking script for initializing omega
 * tracking object, default to tracking script for development environment
 * if not specified.
 *
 * @see {@link https://wiki.corp.adobe.com/display/OMEGA/Adobe+Data+Layer+Schema#AdobeDataLayerSchema-digitalData}
 * @private
 */
/* eslint-enable max-len */
function attachHeader(win: Window, config: {script: string; solution: string; version: string}): void {
  const {_satellite, digitalData, document} = win;
  const headElement = document.getElementsByTagName('head')[0];

  // attach analytics digitalData object, preserving an existing nonce if already present
  const nonce = digitalData?.nonce;
  win.digitalData = {
    ...digitalData,
    event: [],
    nonce: nonce || uuidv4(),
    page: {
      autoTrack: false,
      solution: {
        name: config.solution,
        version: config.version,
      },
    },
    user: {},
  } as any;

  if (
    _satellite?.buildInfo ||
    document.querySelectorAll('script[src*="assets.adobedtm.com"],script[src*="/static/launch"]').length
  ) {
    // Launch already embedded, return.
    return;
  }

  // attach omega tracking script
  const trackingScriptElement = document.createElement('script');
  trackingScriptElement.setAttribute('src', config.script);
  trackingScriptElement.setAttribute('async', '');
  trackingScriptElement.setAttribute('crossorigin', '');
  trackingScriptElement.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  headElement.appendChild(trackingScriptElement);
}

/**
 * Initialize the Omega scripts on the page/application as a singleton.
 *
 * @param {AdobeMetricsRuntime} adobeMetrics Adobe Metrics object.
 * @param {Window} win Window object.
 * @param {object} config A configuration object for the desired state.
 * @param {string} config.script Launch script location.
 * @param {string} config.solution Solution name.
 * @param {string} config.version Solution version.
 */
export default function initAnalytics(
  adobeMetrics: AdobeMetricsRuntime,
  win: Window,
  config?: {script: string; solution: string; version: string},
): void {
  if (!config) {
    return; // do nothing if no config
  }
  // do nothing if already initialized
  if ((adobeMetrics as any).init.analytics) {
    return;
  }

  attachHeader(win, config);
  (adobeMetrics as any).init.analytics = Object.assign({}, config);
}
